import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Disclaimer from "@/components/Disclaimer";
import { Pill, CalendarHeart, GraduationCap, ExternalLink, Sparkles, Stethoscope, Repeat } from "lucide-react";

const mediaUrl = (id) => (id ? `${API}/media/${id}` : null);

function ImageBox({ id, alt, fallbackIcon: Icon }) {
  const url = mediaUrl(id);
  return (
    <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-[#F6ECF0] flex items-center justify-center">
      {url ? (
        <img src={url} alt={alt} className="h-full w-full object-cover" loading="lazy" />
      ) : (
        <Icon className="h-10 w-10 text-[#C9A2B5]" strokeWidth={1.5} />
      )}
    </div>
  );
}

export default function Programs() {
  const [treatments, setTreatments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [cfg, setCfg] = useState({});

  useEffect(() => {
    api.get("/treatments").then((r) => setTreatments(r.data)).catch(() => {});
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  return (
    <AppLayout>
      <div className="animate-fade-up" data-testid="programs-page">
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">BloomHer</span>
        <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">Treatments & Programs</h1>
        <p className="mt-3 text-[#6E5862] max-w-2xl">Your prescriptions, telehealth scheduling, and signature courses — all in one beautiful place.</p>
      </div>

      {/* Prescriptions */}
      <section className="mt-12" data-testid="section-treatments">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-wine/10 flex items-center justify-center"><Pill className="h-5 w-5 text-wine" strokeWidth={1.5} /></div>
          <div>
            <h2 className="font-serif text-3xl text-[#3A1230]">Prescriptions</h2>
            <p className="text-sm text-[#9B7E8D]">Physician-guided treatments fulfilled through our partner pharmacy.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {treatments.map((t) => (
            <div key={t.id} data-testid={`treatment-${t.id}`} className="bg-white rounded-3xl border border-taupe p-5 flex flex-col">
              <ImageBox id={t.image_id} alt={t.name} fallbackIcon={Pill} />
              <h3 className="font-serif text-2xl text-[#3A1230] mt-4">{t.name}</h3>
              {t.description && <p className="text-sm text-[#6E5862] mt-1.5 flex-1">{t.description}</p>}
              <div className="mt-4 flex items-center justify-between gap-3">
                {t.show_price && t.price ? <span className="font-serif text-xl text-wine">{t.price}</span> : <span />}
                {t.link ? (
                  <a href={t.link} target="_blank" rel="noreferrer" data-testid={`treatment-start-${t.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-wine px-5 py-2.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                    Start <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <span className="text-xs rounded-full bg-[#F6ECF0] px-3 py-1.5 text-[#9B7E8D]">Coming soon</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Telehealth */}
      <section className="mt-14" data-testid="section-telehealth">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-wine/10 flex items-center justify-center"><Stethoscope className="h-5 w-5 text-wine" strokeWidth={1.5} /></div>
          <div>
            <h2 className="font-serif text-3xl text-[#3A1230]">Telehealth</h2>
            <p className="text-sm text-[#9B7E8D]">Every journey begins with a consult. Returning clients can book weekly visits.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-3xl border border-taupe p-7">
            <Sparkles className="h-6 w-6 text-wine mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-[#3A1230]">New here? Start with a consult</h3>
            <p className="text-sm text-[#6E5862] mt-1.5">Your first 1:1 telehealth visit to build your personalized plan.</p>
            <a href={cfg.healthie_booking_url} target="_blank" rel="noreferrer" data-testid="telehealth-consult"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
              Book my consult <ExternalLink className="h-4 w-4" />
            </a>
          </div>
          <div className="bg-gradient-to-br from-[#F7E9EF] to-white rounded-3xl border border-taupe p-7">
            <Repeat className="h-6 w-6 text-wine mb-3" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-[#3A1230]">Existing client? Weekly scheduling</h3>
            <p className="text-sm text-[#6E5862] mt-1.5">Already a BloomHer client — book your weekly telehealth check-in.</p>
            <a href={cfg.healthie_weekly_url} target="_blank" rel="noreferrer" data-testid="telehealth-weekly"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-wine px-6 py-3 text-sm font-medium text-wine hover:bg-[#F6ECF0] transition-all">
              Schedule weekly visit <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Courses & Workshops */}
      <section className="mt-14" data-testid="section-courses">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-2xl bg-wine/10 flex items-center justify-center"><GraduationCap className="h-5 w-5 text-wine" strokeWidth={1.5} /></div>
          <div>
            <h2 className="font-serif text-3xl text-[#3A1230]">Courses & Workshops</h2>
            <p className="text-sm text-[#9B7E8D]">Signature programs to help you bloom from the inside out.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => (
            <div key={c.id} data-testid={`course-${c.id}`}
              className={`rounded-3xl p-5 flex flex-col ${c.featured ? "bg-white border-2 border-gold shadow-[0_8px_30px_rgba(139,42,82,0.08)]" : "bg-white border border-taupe"}`}>
              {c.featured && (
                <span className="self-start mb-3 inline-flex items-center gap-1 rounded-full bg-gold/15 px-3 py-1 text-xs font-medium text-[#8a6d1f]">
                  <Sparkles className="h-3 w-3" /> Best value bundle
                </span>
              )}
              <ImageBox id={c.image_id} alt={c.title} fallbackIcon={GraduationCap} />
              <h3 className="font-serif text-2xl text-[#3A1230] mt-4">{c.title}</h3>
              {c.description && <p className="text-sm text-[#6E5862] mt-1.5 flex-1">{c.description}</p>}
              <div className="mt-4 flex items-center justify-between gap-3">
                {c.price ? <span className="font-serif text-xl text-wine">{c.price}</span> : <span />}
                {c.link ? (
                  c.link.startsWith("/") ? (
                    <Link to={c.link} data-testid={`course-enroll-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-wine px-5 py-2.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                      {c.cta_label || "Apply"}
                    </Link>
                  ) : (
                    <a href={c.link} target="_blank" rel="noreferrer" data-testid={`course-enroll-${c.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-wine px-5 py-2.5 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                      {c.cta_label || "Enroll"} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )
                ) : (
                  <span className="text-xs rounded-full bg-[#F6ECF0] px-3 py-1.5 text-[#9B7E8D]">Coming soon</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-12">
        <Disclaimer />
      </div>
    </AppLayout>
  );
}
